import { useEffect } from "react";

const MCP_ENDPOINT = "https://api.venuelista.com/mcp";

async function mcpCall(toolName: string, args: Record<string, unknown>) {
  const res = await fetch(MCP_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      jsonrpc: "2.0",
      id: 1,
      method: "tools/call",
      params: { name: toolName, arguments: args },
    }),
  });
  const json = await res.json();
  if (json.error) throw new Error(json.error.message);
  const text = json.result?.content?.[0]?.text;
  return text ? JSON.parse(text) : json.result;
}

const TOOLS = [
  {
    name: "search_venues",
    description:
      "Search for venues on Listaa by keyword, city, country, or venue type. Returns a list of matching venues with id, name, type, city, country, address, capacity, and description.",
    inputSchema: {
      type: "object",
      properties: {
        query:   { type: "string",  description: "Keyword to search in venue name or description" },
        city:    { type: "string",  description: "Filter by city" },
        country: { type: "string",  description: "Filter by country" },
        type:    { type: "string",  description: "Venue type, e.g. nightclub, restaurant, lounge" },
        limit:   { type: "integer", description: "Max results (default 10, max 50)" },
      },
    },
    execute: (args: Record<string, unknown>) => mcpCall("search_venues", args),
  },
  {
    name: "get_venue",
    description: "Get full details for a specific venue by its ID.",
    inputSchema: {
      type: "object",
      required: ["venue_id"],
      properties: {
        venue_id: { type: "string", description: "Venue id returned by search_venues" },
      },
    },
    execute: (args: Record<string, unknown>) => mcpCall("get_venue", args),
  },
  {
    name: "check_availability",
    description:
      "Check whether a venue has reservation capacity on a given date. Returns available (bool), capacity, guests_confirmed, and remaining_spots.",
    inputSchema: {
      type: "object",
      required: ["venue_id", "date"],
      properties: {
        venue_id:     { type: "string",  description: "Venue id" },
        date:         { type: "string",  description: "Date in YYYY-MM-DD format" },
        guests_count: { type: "integer", description: "Number of guests (default 1)" },
      },
    },
    execute: (args: Record<string, unknown>) => mcpCall("check_availability", args),
  },
  {
    name: "create_reservation",
    description: "Create a table reservation at a venue. Returns a reservation_id and status.",
    inputSchema: {
      type: "object",
      required: ["venue_id", "date", "guests_count", "first_name", "last_name", "email"],
      properties: {
        venue_id:     { type: "string"  },
        date:         { type: "string",  description: "YYYY-MM-DD" },
        time:         { type: "string",  description: "Preferred time e.g. 19:00" },
        guests_count: { type: "integer" },
        first_name:   { type: "string"  },
        last_name:    { type: "string"  },
        email:        { type: "string", format: "email" },
        phone:        { type: "string"  },
        notes:        { type: "string",  description: "Special requests" },
      },
    },
    execute: (args: Record<string, unknown>) => mcpCall("create_reservation", args),
  },
  {
    name: "get_events",
    description: "List upcoming public events at a venue.",
    inputSchema: {
      type: "object",
      required: ["venue_id"],
      properties: {
        venue_id: { type: "string"  },
        limit:    { type: "integer", description: "Max results (default 10)" },
      },
    },
    execute: (args: Record<string, unknown>) => mcpCall("get_events", args),
  },
];

/**
 * Registers Listaa's venue tools with the browser's WebMCP API (navigator.modelContext)
 * so AI agents running in the browser can discover and invoke them directly.
 * No-ops silently in browsers that don't support WebMCP yet.
 */
export function useWebMCP() {
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const nav = navigator as any;
    if (!nav.modelContext?.provideContext) return;

    nav.modelContext.provideContext({ tools: TOOLS }).catch(() => {
      // WebMCP registration failure is non-fatal
    });
  }, []);
}
